import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { addMonths } from 'date-fns';
import {
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  alpha,
} from '@mui/material';
import {
  Cached,
  Download,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
  fetchGroupedLegMembersByCrewCode,
  fetchGroupedRosterDetailsByCrewCode,
} from "state/api";
import { filterCrewMember} from "state/helpers";
import { exportToExcel } from 'state/excel';
import { logClientAction } from 'state/logger';
import { hasPermission } from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import NumberControl from 'components/NumberControl';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import DataGridComponent from 'components/DataGridComponent';
import CrewFilterDialog from 'components/CrewFilterDialog';
import RosterDetailsDialog from 'components/RosterDetailsDialog';
import LegMembersDialog from 'components/LegMembersDialog';
import TopBuddyList from './TopBuddyList';


const HIDDEN_COLUMNS = {
  _id: false, 
  post: false, 
  base: false, 
  trainingQuals: false, 
  quals: false, 
  isActive: false,
};
const NON_MOBILE_HIDDEN_COLUMNS = { 
  _id: false, 
  isActive: false 
};

const RISK_COLOR = { 
  CRITICAL: 'error', 
  HIGH: 'warning', 
  MEDIUM: 'default', 
  LOW: 'success' 
};

const RISK_BG    = {
  CRITICAL: alpha('#d32f2f', 0.10),
  HIGH:     alpha('#f57c00', 0.08),
  MEDIUM:   alpha('#fbc02d', 0.06),
};

const TopBuddyFlying = () => {
  useAuthCheck();
  const user = useSelector((state) => state.user); 
  const crewRanksData = useSelector((state) => state.crewRanks);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  
  const canView = hasPermission(user?.permissions, "TopBuddyFlying", "view", true);
  
  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [openLegMembersDialog, setOpenLegMembersDialog] = useState(false);
  const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);
  const [selectedCrewCode, setSelectedCrewCode] = useState(null);
  const [selectedCrewName, setSelectedCrewName] = useState(null);

  const [groupedCrewRosters, setGroupedCrewRosters] = useState([]);

  const [airports, setAirports] = useState([]);
  const [monthOffset, setMonthOffset] = useState(-1);
  const [crewOffset, setCrewOffset] = useState(50);
  const [ports, setPorts] = useState([]);
  const [view, setView] = useState('buddy');
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(null);
  const [crewPairs, setCrewPairs] = useState([]);
  const [crewData, setCrewData] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isDesktop ? NON_MOBILE_HIDDEN_COLUMNS : HIDDEN_COLUMNS
  );
  
  const getMonthLabel = (q) => 
    q < -1 ? "Previous months" :
    q === -1 ? "Previous month" :
    q === 0 ? "Current month" :
    q === 1 ? "Next month" :
    q > 1 ? "Next months" : "";

  const label = getMonthLabel(monthOffset);

  const handleOffSetChange = (newOffSet) => {
    setMonthOffset(newOffSet); 
  };

  useEffect(() => {
    const fetchAirports = async () => {
      try {
        setLoading(true);  
        const res = await api.get('/airports');  
        setAirports(res?.data || []);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAirports();
  }, []);

  const { fromDate, toDate } = useMemo(() => {
    if (monthOffset < 0) {
      return {
        fromDate: addMonths(startDate, monthOffset),
        toDate: endDate,
      };
    }

    return {
      fromDate: startDate,
      toDate: addMonths(endDate, monthOffset),
    };
  }, [startDate, endDate, monthOffset]);
  
  const handleTopBuddyFlying = useCallback(async () => {
    if (!fromDate || !toDate) return [];
      
    try {
      setError(null);
      setLoading(true);

      const url = '/legmembers/topbuddyflying';
      const payload = { 
        startDate: fromDate, 
        endDate: toDate, 
        crewOffset, 
        ports,
      };        
      const res = await api.post(url, payload);
      
      setCrewPairs(res?.data);
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to fetch top crew pairs!");
    } finally {
      setLoading(false);
    }
  }, [
    fromDate, 
    toDate,
    crewOffset, 
    ports,
  ]);

  const handleTopFlying = useCallback(async () => {
    if (!fromDate || !toDate) return [];
      
    try {
      setError(null);
      setLoading(true);

      const url = '/legmembers/topflying';
      const payload = { 
        startDate: fromDate, 
        endDate: toDate, 
        crewOffset, 
        ports,
      };
      
      const res = await api.post(url, payload);
      
      setCrewData(res?.data);
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to fetch top flying!");
    } finally {
      setLoading(false);
    }
  }, [
    fromDate, 
    toDate, 
    crewOffset, 
    ports,
  ]);

  const filteredData = useMemo(() => {
    const data = view === 'individual' ? crewData : crewPairs;

    if (!data || !Array.isArray(data)) return [];
    if (!filters) return data;
    return data.filter(member => filterCrewMember(member, filters));
  }, [crewData, crewPairs, filters, view]);
    
  const handleCountTopFlyings = useCallback(() => {
    switch(view) {
      case 'buddy':
        handleTopBuddyFlying();
        break;
      case 'individual':
        handleTopFlying();
        break;
      default:
        break;
    }

  }, [view, handleTopBuddyFlying, handleTopFlying])

  const handleViewClick = useCallback(async (crewCode, crewName) => {
    setSelectedCrewCode(crewCode);
    setSelectedCrewName(crewName);
    let result = [];

    try {
      result = await fetchGroupedRosterDetailsByCrewCode(crewCode, fromDate, toDate);       
      setGroupedCrewRosters(result);
      setOpenRosterDetailsDialog(true);
      return;
    } catch {      
      setOpenRosterDetailsDialog(false);      
    }

    try {
      result = await fetchGroupedLegMembersByCrewCode(crewCode, fromDate, toDate);
      setGroupedCrewRosters(result);
      setOpenLegMembersDialog(true);
      return;
    } catch (error) {      
      setOpenLegMembersDialog(false);
    } 
  }, [
    fromDate, 
    toDate, 
  ]);

  const handleCloseRosterDetailsDialog = () => {
    setGroupedCrewRosters([]);
    setOpenRosterDetailsDialog(false);
  };
  
  const handleCloseLegMembersDialog = () => {
    setGroupedCrewRosters([]);
    setOpenLegMembersDialog(false);
  };

  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };

  const baseOptions = [...new Set(crewData?.map(member => member.base))].sort();
  
  const postOptions = useMemo(() => (
    [...new Set(crewData.map(m => m.post))]
      .map(post => crewRanksData.find(r => r.rankCode === post))
      .filter(Boolean)
      .sort((a, b) => a.rankPriority - b.rankPriority)
      .map(r => r.rankCode)
  ), [crewData, crewRanksData]);

  const trainingQualOptions = useMemo(() => (
    [...new Set(
      crewData
        .flatMap(member => member.trainingQuals || [])
        .filter(Boolean)
    )].sort()
  ), [crewData]);
  
  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => isMobile ? { width: 60 } : { flex: 1 })(),
    },
    ...(view === 'buddy' ? [
      {
        field: 'buddyRisk',
        headerName: 'Risk',
        width: 90,
        sortable: true,
        renderCell: ({ value }) => value ? (
          <Chip
            label={value}
            size="small"
            color={RISK_COLOR[value] ?? 'default'}
            sx={{ fontSize: '0.6rem' }}
          />
        ) : null,
      },
      {
        field: 'buddyScore',
        headerName: 'Score',
        width: 62,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => value != null ? `${(value * 100).toFixed(0)}%` : '-',
      },
    ] : []),
    ...(view === 'buddy' ? [
      {
        field: "crewA",
        headerName: "CrewA",
        ...(isMobile ? { width: 360 } : { flex: 5 }),
        renderCell: (params) => {
          const member = params.row.crewA;
          
          return (
            <Box
              sx={{
                display: 'flex',          
                alignItems: 'center',     
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                alignContent='flex-start'
                gap={1}
                sx={{
                  color: member?.gender === 'FM' 
                    ? theme.palette.secondary.main 
                    : theme.palette.primary.dark,
                  cursor: "pointer",
                  '&:hover': {
                    color: "text.primary",
                    textDecoration: 'underline',
                  },                  
                }}
                onClick={() => {
                  if (!canView) return;
                  handleViewClick(params.row.crewA.crewCode, params.row.crewA.crewName);
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    width: '38px',
                  }}
                >
                  {member.crewCode}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {member.crewName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                  }}
                >
                  ({member.post} | {member.base}{member.trnDuty && ` | ${member.trnDuty}`})
                </Typography>                
              </Box>
            </Box>
          );
        },
      },
      {
        field: "crewB",
        headerName: "CrewB",
        ...(isMobile ? { width: 360 } : { flex: 5 }),
        renderCell: (params) => {
          const member = params.row.crewB;
          
          return (
            <Box
              sx={{
                display: 'flex',          
                alignItems: 'center',     
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                alignContent='flex-start'
                gap={1}
                sx={{
                  color: member?.gender === 'FM' 
                    ? theme.palette.secondary.main 
                    : theme.palette.primary.dark,
                  cursor: "pointer",
                  '&:hover': {
                    color: "text.primary",
                    textDecoration: 'underline',
                  },
                }}
                onClick={() => {
                  if (!canView) return;
                  handleViewClick(params.row.crewB.crewCode, params.row.crewB.crewName);
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    width: '38px',
                  }}
                >
                  {member.crewCode}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {member.crewName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                  }}
                >
                  ({member.post} | {member.base}{member.trnDuty && ` | ${member.trnDuty}`})
                </Typography>                
              </Box>
            </Box>
          );
        },
      },
    ] : []),
    ...(view === 'individual' ? [
      {
        field: "crewCode",
        headerName: "Code",
        ...(isMobile ? { width: 80 } : { flex: 1 }),
      },    
      {
        field: "crewName",
        headerName: "CrewName",
        cellClassName: "name-column--cell",
        ...(isMobile ? { width: 360 } : { flex: 4 }),
        renderCell: (params) => {
          const gender = params.row.gender; 
          return (
            <Box
              sx={{
                color: gender === 'FM' 
                  ? theme.palette.secondary.main 
                  : theme.palette.primary.dark,
                cursor: "pointer",
                '&:hover': {
                    color: "text.primary",
                    textDecoration: 'underline',
                },
              }}
              onClick={() => {
                if (!canView) return;
                handleViewClick(params.row.crewCode, params.row.crewName);
              }}
            >
              {params.value}
            </Box>
          );
        },
      },
      {
        field: "post",
        headerName: "Post",
        ...(() => {
          return isMobile ? { width: 80 } : { flex: 1 };
        })(),
      },
      {
        field: "base",
        headerName: "Base",
        ...(() => {
          return isMobile ? { width: 80 } : { flex: 1 };
        })(),
      },
      {
        field: "quals",
        headerName: "Quals",
        ...(() => {
          return isMobile ? { width: 200 } : { flex: 3 };
        })(),
      },
    ] : []),    
    {
      field: "count",
      headerName: "Count",
      align: "center",
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },
    {
      field: "arrList",
      headerName: "ArrivalList",
      sortable: false,
      ...(isMobile ? { width: 100 } : { flex: 1.8 }),
      renderCell: (params) => {
        const value = params.value || {};

        const entries = Object.entries(value).sort((a, b) => b[1] - a[1]);

        if (!entries.length) return "";

        return (
          <Box
            sx={{
              height: '100%',
              alignItems: 'flex-start',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
            style={{ lineHeight: 1.4 }}
          >
            {entries.map(([k, v]) => (
              <div key={k}>{k}: {v}</div>
            ))}
          </Box>
        );
      },
    },
  ], [
    canView, 
    theme, 
    isMobile, 
    view, 
    handleViewClick
  ]);
    


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title={view === 'buddy' ? "Top Buddy Flying" : "Top Flying"}
          subtitle={
            loading ? 'Loading...'
            : view === 'buddy' && filteredData.length > 0
              ? (
                  <Box 
                    display="flex" 
                    flexWrap="wrap" 
                    alignItems="center"
                    gap={0.75} 
                  >
                    {['CRITICAL', 'HIGH', 'MEDIUM'].map((risk) => {
                      const cnt = filteredData.filter((p) => p.buddyRisk === risk).length;
                      if (!cnt) return null;
                      return (
                        <Chip
                          key={risk}
                          label={`${risk}: ${cnt}`}
                          variant="outlined"
                          size="small"
                          color={RISK_COLOR[risk]}
                          sx={{ fontSize: '0.65rem' }}
                        />
                      );
                    })}
                    <Chip
                      label={`Total: ${filteredData.length}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Box>
                )
              : 'Anomaly Detection'
          }
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && (
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title={label} arrow>
                  <span>
                    <NumberControl
                      min={-35}
                      max={35}
                      value={monthOffset}
                      onChange={handleOffSetChange} 
                      textFieldWidth={45}
                    />
                  </span>
                </Tooltip>                
                <Divider
                  orientation="vertical"
                  flexItem
                />
                <Tooltip title="Top crew flyings" arrow>
                  <span>
                    <Box display="flex" alignItems="center" gap={1}>
                      <NumberControl
                        min={1}
                        max={99999}
                        value={crewOffset}
                        onChange={(value) => {
                          setCrewOffset(value); 
                        }} 
                        textFieldWidth={45}
                      />      
                    </Box>      
                  </span>     
                </Tooltip>   
              </Box>
            )}           

            <FormControl size="small">
              <InputLabel>Port</InputLabel>
              <Select
                multiple
                label="Port"
                name="port"
                value={ports}
                sx={{ 
                  minWidth: isMobile ? '6rem' : '6.6rem',
                  maxWidth: isMobile ? '18rem' : '18.6rem',
                }}
                onChange={(e) => setPorts(e.target.value)}
              >
                {/* <MenuItem key='none' value=''>None</MenuItem> */}
                {airports
                  ?.slice() 
                  ?.sort((a, b) => a.iataCode.localeCompare(b.iataCode))
                  ?.map((a, index) => (                                    
                    <MenuItem key={a._id || index} value={a.iataCode}>
                      {`${a.iataCode}${a.countryCode ? ` - ${a.countryCode}` : ''}`}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>

            {!isMobile && (
              <Box display="flex" alignItems="center" gap={1}>
                <FormControl size='small'>
                  <InputLabel>View</InputLabel>
                  <Select
                    label="View"
                    value={view}
                    onChange={(e) => setView(e.target.value)}
                    sx={{ width: isMobile ? '6rem' : '6.6rem' }}
                  >
                    <MenuItem value="buddy">Buddy</MenuItem>
                    <MenuItem value="individual">Individual</MenuItem>
                  </Select>
                </FormControl>
              </Box>              
            )}   

            <Tooltip title="Count top flyings" arrow>
              <span>
                <StyledButton 
                  onClick={handleCountTopFlyings}
                >
                  <Cached />
                </StyledButton>
              </span>
            </Tooltip> 

            {!isMobile && (
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title="Download Excel file" arrow>
                  <span>
                    <StyledButton 
                      onClick={() => {
                        exportToExcel(view === 'individual' ? crewData : crewPairs, "BuddyFlying");
                        logClientAction("EXPORT_REPORT", { fileName: "TopBuddyFlying", format: "Excel", startDate, endDate });
                      }}
                    >
                      <Download />
                    </StyledButton>
                  </span>              
                </Tooltip>
              </Box>              
            )}
            
            <FilterButton
              filters={filters}
              onClick={handleFilterOpen}
            />
          </Box>
        )}                    
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box
            ref={scrollRef} 
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <TopBuddyList
                rows={filteredData}
                view={view}
                canView={canView}
                onViewClick={handleViewClick}
              />
            ) : (
              <DataGridComponent
                rows={filteredData}
                columns={columns}
                loading={loading}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                props={{
                  initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                  getRowClassName: ({ row }) =>
                    view === 'buddy' && row.buddyRisk ? `risk-${row.buddyRisk}` : '',
                  sx: {
                    '& .risk-CRITICAL': { bgcolor: RISK_BG.CRITICAL },
                    '& .risk-HIGH':     { bgcolor: RISK_BG.HIGH },
                    '& .risk-MEDIUM':   { bgcolor: RISK_BG.MEDIUM },
                  },
                }}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      {isFilterDialogOpen &&
        <CrewFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          postOptions={postOptions}
          baseOptions={baseOptions}
          trainingQualOptions={trainingQualOptions}
          currentFilters={filters}
        />
      }
      
      {openRosterDetailsDialog &&
        <RosterDetailsDialog 
          open={openRosterDetailsDialog}
          onClose={handleCloseRosterDetailsDialog}
          crewCode={selectedCrewCode}
          crewName={selectedCrewName}
          crewRosters={groupedCrewRosters}
        />
      }

      {openLegMembersDialog &&
        <LegMembersDialog 
          open={openLegMembersDialog}
          onClose={handleCloseLegMembersDialog}
          crewCode={selectedCrewCode}
          crewName={selectedCrewName}
          crewLegs={groupedCrewRosters}
        />
      }
    </Box>
  );
};

export default TopBuddyFlying;
